// Tudo passa pelo servidor: tabela `professionals` (photo_url) pelo PROXY
// autenticado (/api/db); dados fk_* pelo cofre /api/data (bucket PRIVADO);
// fotos/capas por link assinado (/api/files). Nada usa mais a chave pública
// pra gravar no Storage (02/10/2026).
import { SUPABASE_URL as PROXY_BASE, restHeaders, sessionToken } from './secureRest.js';
import { uploadViaServer } from './fileUpload.js';


// Decodifica dataURL base64 em Blob SEM usar fetch(dataURL) — necessário
// porque o CSP `connect-src` do app NÃO permite `data:`, então o atalho
// `fetch(base64).then(r=>r.blob())` é bloqueado pelo browser.
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/data:([^;]+)/) || [, 'image/jpeg'])[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Upload base64 photo (from cropper) → link assinado do servidor (gestão).
export async function uploadPhotoBase64(base64Data, professionalId) {
  const blob = dataUrlToBlob(base64Data);
  return uploadViaServer('photo', professionalId, blob);
}

// Upload cover photo de UNIDADE (unit-covers/<unitId>-<ts>.jpg). Aceita base64 ou Blob.
export async function uploadUnitCoverBase64(base64Data, unitId) {
  const blob = typeof base64Data === 'string' && base64Data.startsWith('data:')
    ? dataUrlToBlob(base64Data)
    : base64Data; // já é Blob/File
  return uploadViaServer('unit-cover', unitId, blob);
}

// Save photo URL to professionals table
export async function savePhotoUrl(professionalId, photoUrl) {
  const res = await fetch(`${PROXY_BASE}/rest/v1/professionals`, {
    method: 'POST',
    headers: restHeaders({ 'Prefer': 'resolution=merge-duplicates' }),
    body: JSON.stringify({ id: professionalId, photo_url: photoUrl }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Save failed: ${err}`);
  }
}

// Load all photo URLs from professionals table
export async function loadPhotos() {
  const res = await fetch(`${PROXY_BASE}/rest/v1/professionals?select=id,photo_url`, {
    headers: restHeaders(),
  });
  if (!res.ok) return {};
  const data = await res.json();
  const map = {};
  data.forEach(row => { if (row.photo_url) map[row.id] = row.photo_url; });
  return map;
}

// ─── Cloud Data Sync (JSON files in Supabase Storage) ───

// ⚠️ fk_users REMOVIDO daqui — agora é privado (server-only via /api/auth-*).
// Senhas hasheadas, mas o arquivo SAI da rota /public/ pra não ser exposto.
const CLOUD_DATA_KEYS = [
  'fk_units', 'fk_professionals', 'fk_performance',
  // fk_services e fk_packages REMOVIDOS — viraram tabelas Postgres acessadas via
  // /api/services-* e /api/packages-*. Devices antigos não conseguem mais
  // empurrar versões stale do array (não tem mais cloud sync, fonte é o DB).
  'fk_unit_performance',
  'fk_daily_revenue', 'fk_unit_metas', 'fk_holidays', 'fk_unit_config',
  'fk_weekly_duels',             // pareamentos da semana + vencedores históricos
  'fk_prof_challenges',          // desafios definidos pelo admin → vistos pelos profs
  'fk_achievements_v1',          // conquistas desbloqueadas por prof
  'fk_engagement_v1',            // logins e tempo de uso (visto pelo admin)
  // Avec API caches — populados PELO ADMIN, lidos por todos.
  // Evita que cada device martele a API (rate limit).
  'fk_avec_cache',               // cache mensal (ano-mês → unidades)
  'fk_avec_snapshots',           // snapshots de meses fechados
  'fk_avec_daily_cache',         // cache diário do mês corrente
  'fk_avec_last_sync_at',        // timestamp do último sync (TTL distribuído)
];

// Permite checar de fora quais chaves devem sincronizar
export { CLOUD_DATA_KEYS };

// 🔒 Chaves que NUNCA podem ir pro bucket público (dados sensíveis/legados).
// Foram migradas pra Postgres (acesso autenticado) mas caminhos antigos ainda
// chamam set()→cloudSave com elas (ex: o botão legado de resetar senha re-subia
// fk_users público; saveUsers no boot idem). Bloquear aqui impede re-criação do
// arquivo público depois que ele for deletado do bucket.
//   - fk_users: emails+roles (server-only via /api/auth-*)
//   - fk_clients/appointments/comandas/caixas: PII de cliente (Postgres)
//   - fk_services/fk_packages: catálogo (Postgres via /api/services|packages-*)
const NEVER_CLOUD_KEYS = new Set([
  'fk_users',
  'fk_clients', 'fk_appointments', 'fk_comandas', 'fk_caixas',
  'fk_services', 'fk_packages',
]);
export { NEVER_CLOUD_KEYS };

// Chaves de cadastro críticas — nunca devem ser "esvaziadas" por save.
// Se o app TENTAR mandar array vazio nessas, BLOQUEIA o upload.
// Foi assim que packages/services sumiram historicamente: aba antiga com
// cache zerado → set([]) → cloudSave([]) → cloud sobrescrita com vazio.
// fk_services e fk_packages NÃO entram mais (foram pra Postgres).
// Cobre arrays vazios E objetos vazios (Pacote D).
const PROTECTED_NON_EMPTY_KEYS = new Set([
  // Cadastros (arrays):
  'fk_units',
  'fk_users',
  'fk_professionals',
  'fk_prof_challenges',
  'fk_unit_metas',
  'fk_unit_config',
  'fk_holidays',
  // Performance/histórico (arrays — Pacote D):
  'fk_performance',
  'fk_unit_performance',
  'fk_daily_revenue',
  // Objetos versionados (Pacote D):
  'fk_weekly_duels',
  'fk_achievements_v1',
  'fk_engagement_v1',
]);

// Internal: sobe o dado pro cofre do servidor (/api/data → bucket PRIVADO).
// Desde 02/10/2026 as travas (anti-esvaziamento, merge de analytics, quem pode
// gravar o quê) rodam NO SERVIDOR (api/_lib/data-merge.js) — valem pra todo
// device, inclusive aba velha. Retorno: true = resolvido (subiu OU foi recusado
// de propósito e não deve ficar em retry); false = falhou, tentar de novo.
//
// `keepalive: true` permite que o fetch sobreviva ao fechamento da tab — mas o
// browser só aceita até 64KiB (somados entre requests em voo), por isso o
// corte em 48KB (fk_performance ~565KB sobe sem keepalive).
// MERGE_JANELA_SO_LOCAL (29/10/2026, sugestão da outra sessão): chave cujo último
// envio o servidor recusou (403, ex.: recepcionista e fk_unit_metas). O que foi
// gravado aqui nunca chega ao cofre, então a janela de 5 min do merge
// (utils/storage.js) não o protege: o cofre vence na hora.
const _envioRecusado = new Set();
export const envioRecusado = (key) => _envioRecusado.has(key);

async function _cloudUpload(key, data, token) {
  // 🔒 CAMADA 0: chave sensível/legada nunca sobe por aqui.
  if (NEVER_CLOUD_KEYS.has(key)) {
    console.warn(`[cloudSave] 🔒 upload de "${key}" bloqueado (chave sensível/legada)`);
    return true;
  }
  // Sem sessão (tela de login, página pública) NADA sobe: o servidor recusaria
  // e um retry pós-login empurraria cópia velha/seed por cima do cloud.
  if (!token) return true;

  try {
    const blob = new Blob([JSON.stringify({ data, savedAt: new Date().toISOString() })], { type: 'application/json' });
    const keepalive = blob.size < 48_000;
    const res = await fetch(`/api/data?key=${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: blob,
      keepalive,
    });
    if (res.ok) {
      _envioRecusado.delete(key);
      const body = await res.json().catch(() => null);
      // O servidor fez UNIÃO com o que já existia → converge o local pro
      // resultado (se nenhum save novo entrou na fila nesse meio tempo).
      if (body && body.merged !== undefined && !_pendingTimers[key]) {
        try { localStorage.setItem(key, JSON.stringify(body.merged)); } catch { /* quota */ }
      }
      return true;
    }
    if (res.status === 409) {
      // 🛡️ CAMADA 1 (servidor): tentou salvar VAZIO por cima de dado existente.
      const body = await res.json().catch(() => ({}));
      console.error(`[cloudSave] 🚫 BLOQUEADO ${key}: tentou salvar VAZIO mas o cloud tem ${body.cloudCount ?? '?'} itens.`);
      try {
        window.dispatchEvent(new CustomEvent('fk:zero-overwrite-blocked', {
          detail: { key, cloudCount: body.cloudCount },
        }));
      } catch {}
      return false;
    }
    if (res.status === 401 || res.status === 403) {
      // Sessão expirada ou sem permissão pra essa chave: descarta (o próximo
      // loadFromCloud traz a versão do cloud). Não fica em retry.
      if (res.status === 403) _envioRecusado.add(key);
      console.warn(`[cloudSave] ${key}: recusado pelo servidor (HTTP ${res.status}) — mantendo o cloud`);
      return true;
    }
    console.warn(`[cloudSave] upload falhou ${key}: HTTP ${res.status}`);
    return false;
  } catch (err) {
    console.warn(`[cloudSave] upload exception ${key}:`, err?.message || err);
    return false;
  }
}

// ─── Merge-antes-de-subir ────────────────────────────────────────────────────
// Foi pro servidor (api/_lib/data-merge.js: MERGE_ON_WRITE) em 02/10/2026 —
// mesmas regras (snapshots: cloud vence; cache mensal: local vence; unit perf
// por updatedAt estrito; performance: união sem ids fantasma/seed/mês futuro).

// Debounced cloud save — agora 300ms (antes era 1s).
// Mantém debounce pra agrupar saves rápidos (ex: editar 5 metas seguidas),
// mas curto o suficiente pra que o usuário sinta como instantâneo.
//
// CRITICAL: rastreia "dirty keys" — chaves cujo upload foi tentado mas
// AINDA NÃO foi confirmado com sucesso. Outras partes do app (loadFromCloud)
// consultam isso pra NÃO sobrescrever localStorage com versão antiga do
// cloud — caso contrário um upload falho fazia o auto-refresh apagar dados
// recém-cadastrados (ex: profissional sumindo do sistema).
//
// Estados:
//   _pendingTimers[key]: debounce ativo (vai disparar upload em breve)
//   _uploadingKeys: upload em andamento agora
//   _dirtyKeys: TENTADO mas FALHOU (precisa retry) OU pendente OU em curso
//   _retryAttempts[key]: contador de tentativas falhas consecutivas
const _pendingTimers = {};
const _tokenAtEnqueue = {}; // sessão de quem salvou (o logout limpa antes do debounce)
const _dirtyKeys = new Set();
const _uploadingKeys = new Set();
const _retryAttempts = {};
const MAX_RETRIES = 5;

export function isDirty(key) {
  return _dirtyKeys.has(key);
}
export function getDirtyKeys() {
  return new Set(_dirtyKeys);
}

// Tenta upload da última versão do localStorage, com retry interno.
// Retorna true se subiu, false se falhou após todas as tentativas.
async function _attemptUpload(key) {
  _uploadingKeys.add(key);
  let ok = false;
  try {
    const latest = JSON.parse(localStorage.getItem(key) || 'null');
    if (latest === null) {
      // Sem dado local pra subir — considera "ok" pra liberar dirty
      ok = true;
    } else {
      ok = await _cloudUpload(key, latest, _tokenAtEnqueue[key] || sessionToken());
    }
  } catch (err) {
    console.warn(`[cloudSave] erro lendo localStorage ${key}:`, err?.message);
    ok = false;
  } finally {
    _uploadingKeys.delete(key);
  }
  if (ok) {
    delete _retryAttempts[key];
    // Só remove de dirty se NENHUM novo save foi enfileirado durante o upload
    if (!_pendingTimers[key]) _dirtyKeys.delete(key);
  } else {
    _retryAttempts[key] = (_retryAttempts[key] || 0) + 1;
    // Mantém dirty pra impedir sobrescrita por loadFromCloud
    // (loadFromCloud checa isDirty antes de aplicar dado vindo do cloud)
  }
  return ok;
}

export function cloudSave(key, data) {
  const token = sessionToken();
  if (token) _tokenAtEnqueue[key] = token;
  _dirtyKeys.add(key);
  return new Promise((resolve) => {
    if (_pendingTimers[key]) clearTimeout(_pendingTimers[key]);
    _pendingTimers[key] = setTimeout(async () => {
      delete _pendingTimers[key];
      const ok = await _attemptUpload(key);
      resolve(ok);
    }, 300);
  });
}

// Background retry: a cada 30s, tenta re-uploadar chaves que ainda estão
// dirty (último upload falhou). Após MAX_RETRIES, LIBERA o dirty: manter a
// chave suja pra sempre deixava o device SURDO pro cloud a sessão inteira
// (loadFromCloud pula chave dirty) — foi assim que cada computador congelou
// numa cópia de uma época. Depois de esgotar os retries, convergir com o
// cloud vale mais do que proteger uma edição local que não consegue subir.
let _retryInterval = null;
function _startBackgroundRetry() {
  if (_retryInterval) return;
  _retryInterval = setInterval(async () => {
    for (const key of [..._dirtyKeys]) {
      // Pula se já tem upload em curso ou debounce ativo
      if (_uploadingKeys.has(key) || _pendingTimers[key]) continue;
      // Esgotou os retries → desiste E libera o apply do cloud
      if ((_retryAttempts[key] || 0) >= MAX_RETRIES) {
        console.warn(`[cloudSave] ${key}: upload falhou ${MAX_RETRIES}x — liberando pra voltar a receber o cloud`);
        _dirtyKeys.delete(key);
        delete _retryAttempts[key];
        continue;
      }
      console.info(`[cloudSave] retry ${key} (tentativa ${(_retryAttempts[key] || 0) + 1}/${MAX_RETRIES})`);
      await _attemptUpload(key);
    }
  }, 30_000);
}
// Liga ao carregar o módulo
if (typeof window !== 'undefined') {
  _startBackgroundRetry();
  // Quando volta a ficar online, retomar imediatamente
  window.addEventListener('online', () => {
    for (const key of [..._dirtyKeys]) {
      if (_uploadingKeys.has(key) || _pendingTimers[key]) continue;
      _retryAttempts[key] = 0; // reset contador ao voltar online
      _attemptUpload(key);
    }
  });
}

// Força flush IMEDIATO de todos os uploads pendentes:
//   1. Dispara qualquer debounce em andamento (em vez de esperar os 300ms)
//   2. Aguarda uploads em curso até o timeout
//
// Crítico em beforeunload/pagehide — sem isso, debounce pendente é
// descartado quando o tab fecha e a mudança é perdida.
export async function flushPendingUploads(timeoutMs = 5000) {
  // 1) Dispara TODOS debounces pendentes agora
  const pendingKeys = Object.keys(_pendingTimers);
  for (const key of pendingKeys) {
    clearTimeout(_pendingTimers[key]);
    delete _pendingTimers[key];
    _attemptUpload(key).catch(() => {});
  }
  // 2) Espera tudo terminar
  const start = Date.now();
  while (_dirtyKeys.size > 0 || _uploadingKeys.size > 0) {
    if (Date.now() - start > timeoutMs) break;
    await new Promise((r) => setTimeout(r, 100));
  }
}

// Load a single key from cloud — CONDITIONAL FETCH com ETag/Last-Modified.
//
// Bug original: o `?t=${Date.now()}` bustava cache → cada refresh baixava
// 1.73 MB (soma de 17 JSONs). 50 users × 60s polling = ~5 GB/mês de cached
// egress no Supabase Free. Estourou.
//
// Fix: persistimos ETag + Last-Modified em localStorage por key. Próximo
// fetch manda If-None-Match + If-Modified-Since. Storage responde:
//   - 304 Not Modified (corpo vazio, ~200 bytes total) → usa cache local
//   - 200 OK + novo body → atualiza cache + ETag
//
// Resultado em prod (assumindo 90% dos refreshes não tem mudança):
//   1.73 MB × 60s × 8h × 50 users = 41 GB/dia → 4.1 GB/dia (-90%)
const ETAG_PREFIX = '__cloud_etag_';
const LASTMOD_PREFIX = '__cloud_lastmod_';

function getCachedMeta(key) {
  try {
    return {
      etag: localStorage.getItem(ETAG_PREFIX + key) || null,
      lastModified: localStorage.getItem(LASTMOD_PREFIX + key) || null,
    };
  } catch {
    return { etag: null, lastModified: null };
  }
}

function setCachedMeta(key, etag, lastModified) {
  try {
    if (etag) localStorage.setItem(ETAG_PREFIX + key, etag);
    if (lastModified) localStorage.setItem(LASTMOD_PREFIX + key, lastModified);
  } catch { /* quota cheia? ignora */ }
}

// 23/10/2026 (SEED_TRAVADO, "Resetar Dados" que só limpa o cache local):
// esquece o ETag da chave, para a próxima leitura trazer a cópia inteira.
export function esquecerCloudMeta(key) {
  try {
    localStorage.removeItem(ETAG_PREFIX + key);
    localStorage.removeItem(LASTMOD_PREFIX + key);
  } catch { /* armazenamento bloqueado: a leitura só não vira "não mudou" */ }
  delete _pendingCloudMeta[key];
}

// Sinal especial: "não mudou, segue com o que já está em localStorage".
// loadFromCloud entende isso e SKIPA aplicar (não sobrescreve sem motivo).
export const CLOUD_NOT_MODIFIED = Symbol('cloud_not_modified');
// Sinal: "não deu pra consultar" (sem sessão, servidor fora, sem permissão).
// Diferente de AUSENTE: loadFromCloud NÃO aplica nem faz backfill dessa chave.
export const CLOUD_UNAVAILABLE = Symbol('cloud_unavailable');

// ETag PENDENTE: o meta de cache só pode ser persistido DEPOIS que o dado foi
// efetivamente APLICADO no localStorage. Antes, o cloudLoad gravava o ETag na
// hora do fetch — se o loadFromCloud decidisse PULAR o apply (chave dirty,
// guard de shrink), o device passava a receber 304 de uma versão que NUNCA
// aplicou e ficava congelado no passado pra sempre (o ETag sobrevive ao reload,
// o motivo do skip não). Quem aplica confirma via confirmCloudMeta(key).
const _pendingCloudMeta = {};
export function confirmCloudMeta(key) {
  const m = _pendingCloudMeta[key];
  if (m) {
    setCachedMeta(key, m.etag, m.lastModified);
    delete _pendingCloudMeta[key];
  }
}

export async function cloudLoad(key) {
  const token = sessionToken();
  if (!token) return CLOUD_UNAVAILABLE;
  try {
    const { etag, lastModified } = getCachedMeta(key);
    const headers = { 'x-session-token': token };
    if (etag) headers['If-None-Match'] = etag;
    if (lastModified) headers['If-Modified-Since'] = lastModified;

    const res = await fetch(`/api/data?key=${encodeURIComponent(key)}`, { headers, cache: 'no-store' });

    if (res.status === 304) return CLOUD_NOT_MODIFIED;
    if (res.status === 404) return null;
    if (!res.ok) return CLOUD_UNAVAILABLE;

    const newEtag = res.headers.get('etag');
    const newLastMod = res.headers.get('last-modified');
    const json = await res.json();
    _pendingCloudMeta[key] = { etag: newEtag, lastModified: newLastMod };
    return json.data || null;
  } catch {
    return CLOUD_UNAVAILABLE;
  }
}

// Load ALL data keys from cloud at once — UMA chamada (/api/data op:'load')
// com o ETag de cada chave; o servidor responde 'not-modified' quando nada
// mudou. Chave 'defer' (resposta grande demais) é buscada sozinha.
// Mantém CLOUD_NOT_MODIFIED / CLOUD_UNAVAILABLE no results pra que
// loadFromCloud saiba o que NÃO reaplicar nem fazer backfill.
export async function cloudLoadAll() {
  const results = {};
  const token = sessionToken();
  const unavailableAll = () => {
    for (const key of CLOUD_DATA_KEYS) results[key] = CLOUD_UNAVAILABLE;
    return results;
  };
  if (!token) return unavailableAll();

  let body;
  try {
    const res = await fetch('/api/data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: JSON.stringify({
        op: 'load',
        keys: CLOUD_DATA_KEYS.map(key => ({ key, ...getCachedMeta(key) })),
      }),
      cache: 'no-store',
    });
    if (!res.ok) return unavailableAll();
    body = await res.json();
  } catch {
    return unavailableAll();
  }

  const deferred = [];
  for (const key of CLOUD_DATA_KEYS) {
    const r = body?.results?.[key];
    if (!r || r.status === 'error') { results[key] = CLOUD_UNAVAILABLE; continue; }
    if (r.status === 'not-modified') { results[key] = CLOUD_NOT_MODIFIED; continue; }
    if (r.status === 'missing') continue; // ausente de verdade
    if (r.status === 'defer') { deferred.push(key); continue; }
    if (r.status === 'ok') {
      const data = r.json?.data;
      if (data) {
        _pendingCloudMeta[key] = { etag: r.etag, lastModified: r.lastModified };
        results[key] = data;
      }
    }
  }
  await Promise.all(deferred.map(async (key) => {
    const data = await cloudLoad(key);
    if (data !== null) results[key] = data;
  }));
  return results;
}

// Push ALL localStorage data to cloud (initial sync)
export async function cloudSaveAll() {
  const promises = CLOUD_DATA_KEYS.map(async (key) => {
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const data = JSON.parse(raw);
        await cloudSave(key, data);
      } catch {}
    }
  });
  await Promise.all(promises);
}
